import { DEVICE_MODE_AUTO_ID, DeviceTypeCode } from 'app/shared/enums/device-type-code';
import { LocationCardState } from '../../reducers';
import {
    getSensorKeyById,
    Mp1SensorIds,
    Mp1SensorTitles,
    Mp2SensorIds,
    Mp2SensorTitles,
    TritonInfoFieldId,
    TritonSectionIds,
    TritonSensors,
} from './base-config-models';
import { ChannelElement, SensorElement, SensorPreference } from '../../../models/location-card.model';
import { NormalizedFormState, normalizeSection } from '../../config.models';
import { mp2basicInfo } from './mp2-basic-info';
import { mp2configureAlarms } from './mp2-alarms';
import { PrimarySensors, SecondarySensors } from '../../../location-card.constants';
import { mp2Flow } from './flow/mp2-flow';
import { QCONTINUITY_ENTITY, QFLUME_ENTITY, QLOOKUP_ENTITY, QWEIR_ENTITY } from 'app/shared/constant';
import { findFieldById } from '../base-utils';
import { BasicInfoCommonFieldIds, SensorTypes } from '../common.models';
import { FieldType, SelectField } from '../../models/field-models';
import { AlarmDevice, DeviceConfig, FlowLossAlarmDevice, SamplerDevice, TritonLocationCardState, TritonMonitorConfiguration } from './models';
import { ValidationRuleType } from '../../models/validator-models';
import { required } from '../../core/validator-builders';
import { extractMp2Payload } from './base-api-data';

export function setTritonSensorPreferences(state: TritonLocationCardState) {
    const tritonInfo = state.formState.config.sections.find((section) => section.id === TritonSectionIds.TritonInfo);

    const channel1Field = tritonInfo.fields[TritonInfoFieldId.Channel1] as SelectField<ChannelElement, number>;
    const channel2Field = tritonInfo.fields[TritonInfoFieldId.Channel2] as SelectField<ChannelElement, number>;
    const mp1Field = tritonInfo.fields[TritonInfoFieldId.MP1];
    const mp2Field = tritonInfo.fields[TritonInfoFieldId.MP2];

    const depth1PreferenceField = tritonInfo.fields[TritonInfoFieldId.DepthSensor1];
    const depth2PreferenceField = tritonInfo.fields[TritonInfoFieldId.DepthSensor2];

    const velocity1PreferenceField = tritonInfo.fields[TritonInfoFieldId.VelocitySensor1];
    const velocity2PreferenceField = tritonInfo.fields[TritonInfoFieldId.VelocitySensor2];

    const sensorPreferenceTitle = tritonInfo.fields[TritonInfoFieldId.SensorPreferencesTitle];
    const sensorPreferenceTitle2 = tritonInfo.fields[TritonInfoFieldId.SensorPreferencesTitle2];

    const sensor1Field = tritonInfo.fields[TritonInfoFieldId.Sensor1] as SelectField<SensorElement, string>;
    const sensor2Field = tritonInfo.fields[TritonInfoFieldId.Sensor2] as SelectField<SensorElement, string>;
    const sensor1Value = sensor1Field.value;
    const sensor2Value = sensor2Field.value;

    const depthPreferencesFirst = sensor1Value ? DeviceTypeCode.getDepthPreferenceBySensor(sensor1Value) : [];
    const velPreferencesFirst = sensor1Value ? DeviceTypeCode.getVelocityPreferenceBySensor(sensor1Value) : [];

    const depthPreferencesSecond = sensor2Value ? DeviceTypeCode.getDepthPreferenceBySensor(sensor2Value) : [];
    const velPreferencesSecond = sensor2Value ? DeviceTypeCode.getVelocityPreferenceBySensor(sensor2Value) : [];

    const channelFor = (channel: number): string => {
        return channel === 1 ? 'CH1' : channel === 2 ? 'CH2' : '';
    };

    const decorateWithCH = (inArr: SensorPreference[], sufix: string) => {
        if (!inArr || !inArr.length) return [];

        return inArr.map((v) => {
            return v.id === DEVICE_MODE_AUTO_ID ? { ...v } : { ...v, text: `${v.text} ${sufix}` };
        });
    };

    const clearDups = (arr: SensorPreference[]) => {
        return arr.filter((v, i, a) => a.findIndex((t) => t.id === v.id) === i);
    };

    const channelFirst = channelFor(channel1Field.value);
    const channelSecond = channelFor(channel2Field.value);

    state.options.depthPreferencesFirst = [];
    state.options.velPreferencesFirst = [];
    state.options.depthPreferencesSecond = [];
    state.options.velPreferencesSecond = [];

    if (mp1Field.value === mp2Field.value && mp1Field.value !== null) {
        if (mp1Field.value === 0) {
            state.options.depthPreferencesFirst = clearDups([
                ...decorateWithCH(depthPreferencesFirst, channelFirst),
                ...decorateWithCH(depthPreferencesSecond, channelSecond),
            ]);
            state.options.velPreferencesFirst = clearDups([
                ...decorateWithCH(velPreferencesFirst, channelFirst),
                ...decorateWithCH(velPreferencesSecond, channelSecond),
            ]);
        } else {
            state.options.depthPreferencesSecond = clearDups([
                ...decorateWithCH(depthPreferencesFirst, channelFirst),
                ...decorateWithCH(depthPreferencesSecond, channelSecond),
            ]);
            state.options.velPreferencesSecond = clearDups([
                ...decorateWithCH(velPreferencesFirst, channelFirst),
                ...decorateWithCH(velPreferencesSecond, channelSecond),
            ]);
        }
    } else {
        if (mp1Field.value === 0) {
            state.options.depthPreferencesFirst = decorateWithCH(depthPreferencesFirst, channelFirst);
            state.options.velPreferencesFirst = decorateWithCH(velPreferencesFirst, channelFirst);
        } else if (mp1Field.value === 1) {
            state.options.depthPreferencesFirst = decorateWithCH(depthPreferencesSecond, channelFirst);
            state.options.velPreferencesFirst = decorateWithCH(velPreferencesSecond, channelFirst);
        }

        if (mp2Field.value === 0) {
            state.options.depthPreferencesSecond = decorateWithCH(depthPreferencesFirst, channelSecond);
            state.options.velPreferencesSecond = decorateWithCH(velPreferencesFirst, channelSecond);
        } else if (mp2Field.value === 1) {
            state.options.depthPreferencesSecond = decorateWithCH(depthPreferencesSecond, channelSecond);
            state.options.velPreferencesSecond = decorateWithCH(velPreferencesSecond, channelSecond);
        }
    }

    depth1PreferenceField.visible = !!state.options.depthPreferencesFirst.length;
    depth2PreferenceField.visible = !!state.options.depthPreferencesSecond.length;
    velocity1PreferenceField.visible = !!state.options.velPreferencesFirst.length;
    velocity2PreferenceField.visible = !!state.options.velPreferencesSecond.length;

    sensorPreferenceTitle.visible = depth1PreferenceField.visible || velocity1PreferenceField.visible ? true : false;
    sensorPreferenceTitle2.visible = depth2PreferenceField.visible || velocity2PreferenceField.visible ? true : false;

    const sensorPreferenceDivider = tritonInfo.fields[TritonInfoFieldId.SensorPreferenceDivider];
    const comportDivider = tritonInfo.fields[TritonInfoFieldId.ComportDivider];

    sensorPreferenceDivider.visible = !!sensorPreferenceTitle2.visible;
    comportDivider.visible = sensorPreferenceTitle2.visible || sensorPreferenceTitle.visible ? true : false;
}

export function sortTritonSections(formState: NormalizedFormState) {
    const basicInfo = formState.config.sections.find((section) => section.id === TritonSectionIds.BasicInfo);
    const tritonInfo = formState.config.sections.find((section) => section.id === TritonSectionIds.TritonInfo);
    const dataDelivery = formState.config.sections.find((section) => section.id === TritonSectionIds.DataDelivery);
    const configureAlarms = formState.config.sections.find(
        (section) => section.id === TritonSectionIds.ConfigureAlarms,
    );
    const sensor1 = formState.config.sections.find(
        (section) =>
            section.metadata && section.metadata.type === SensorTypes.triton && section.metadata.sensorIndex === 0,
    );
    const mp2BasicInfo = formState.config.sections.find((section) => section.id === TritonSectionIds.Mp2BasicInfo);
    const mp2ConfigureAlarms = formState.config.sections.find((section) => section.id === TritonSectionIds.Mp2Alarms);
    const sensor2 = formState.config.sections.find(
        (section) =>
            section.metadata && section.metadata.type === SensorTypes.triton && section.metadata.sensorIndex === 1,
    );

    const dialogActions = formState.config.sections.find((section) => section.id === TritonSectionIds.DialogActions);
    const mp1Field = tritonInfo.fields[TritonInfoFieldId.MP1];
    const mp2Field = tritonInfo.fields[TritonInfoFieldId.MP2];

    const sampler = formState.config.sections.find((section) => section.id === TritonSectionIds.Sampler);

    const flow1 = formState.config.sections.find((section) => section.id === TritonSectionIds.Flow1);
    const flow2 = formState.config.sections.find((section) => section.id === TritonSectionIds.Flow2);
    const advanced = formState.config.sections.find((section) => section.id === TritonSectionIds.Advanced);

    formState.config.sections = [
        basicInfo,
        tritonInfo,
        mp1Field.value === 0 ? sensor1 : null,
        mp2Field.value === 0 ? sensor2 : null,
        configureAlarms,
        dataDelivery,
        flow1,
        advanced,
        mp2BasicInfo,
        mp1Field.value === 1 ? sensor1 : null,
        mp2Field.value === 1 ? sensor2 : null,
        mp2ConfigureAlarms,
        flow2,
        sampler,
        dialogActions,
    ].filter((v) => !!v);
}

export function tritonCheckMp2(state: TritonLocationCardState) {
    const tritonInfo = state.formState.config.sections.find(
        (section) => section.id === TritonSectionIds.TritonInfo,
    );
    const mp2Field = tritonInfo?.fields[TritonInfoFieldId.MP2];
    const shouldHaveMp2 = mp2Field && mp2Field.value === 1;
    const mp2InfoExist = state.formState.config.sections.find(
        (section) => section.id === TritonSectionIds.Mp2BasicInfo,
    );

    if (shouldHaveMp2 && !mp2InfoExist) {
        state.mp2LastBasicInfo = null;

        const normalizedMp2 = normalizeSection(mp2basicInfo(state));
        const normalizedMp2Alarms = normalizeSection(mp2configureAlarms(state));
        const flow2 = normalizeSection(mp2Flow(state));

        state.formState.config.sections.push(normalizedMp2, normalizedMp2Alarms, flow2);
    } else if (!shouldHaveMp2 && mp2InfoExist) {
        state.mp2LastBasicInfo = extractMp2Payload(state);

        state.formState.config.sections = state.formState.config.sections.filter(
            (section) =>
                section.id !== TritonSectionIds.Mp2BasicInfo &&
                section.id !== TritonSectionIds.Mp2Alarms &&
                section.id !== TritonSectionIds.Flow2,
        );
    }
}

export function tritonSensorChangeUpdates(formState: NormalizedFormState) {
    // If long range sensor is selected, we need to make manhole depth field required
    const sensors = formState.config.sections.filter(
        (section) => section.metadata && section.metadata.type === SensorTypes.triton,
    );
    const manholeDepthField = findFieldById(formState, BasicInfoCommonFieldIds.ManholeDepth, FieldType.Number);

    const isLongRangeSensorSelected = sensors.some(
        (sensor) =>
            sensor.id === Mp1SensorIds[TritonSensors.LongRange] || sensor.id === Mp2SensorIds[TritonSensors.LongRange],
    );

    if (isLongRangeSensorSelected && !manholeDepthField.validators.find((v) => v.type === ValidationRuleType.Required)) {
        manholeDepthField.validators.push(required());
    }

    if (!isLongRangeSensorSelected && manholeDepthField.validators.find((v) => v.type === ValidationRuleType.Required)) {
        manholeDepthField.validators = manholeDepthField.validators.filter((v) => v.type !== ValidationRuleType.Required);
    }
}

export function extractTritonSensorsFromConfig(
    config: TritonMonitorConfiguration,
): Exclude<DeviceConfig, AlarmDevice | FlowLossAlarmDevice | SamplerDevice>[] {
    if (!config || !config.devices) return [];

    const allTritonSensors = [...PrimarySensors, ...SecondarySensors];

    return config.devices
        .filter((v) => allTritonSensors.find((x) => x.altValue === v.name))
        .sort((a, b) => (a as { channel: number }).channel - (b as { channel: number }).channel) as Exclude<
        DeviceConfig,
        AlarmDevice | FlowLossAlarmDevice | SamplerDevice
    >[];
}

export function getTritonSensorValue(sensor, isMp1 = true): SensorElement {
    const allTritonSensors = [...PrimarySensors, ...SecondarySensors];

    const configSensor = allTritonSensors.find((x) => x.altValue === sensor.name);

    if (isMp1) {
        return PrimarySensors.find((v) => v.text === configSensor.text);
    }

    return SecondarySensors.find((v) => v.text === configSensor.text);
}

export function getMPvalueBySensorName(sensorName: string): 0 | 1 {
    return (Number(sensorName.match(/(\d)/)[0]) - 1) as 0 | 1;
}

export function tritonMonitorPointChange(formState: NormalizedFormState, index: number) {
    const tritonInfo = formState.config.sections.find((section) => section.id === TritonSectionIds.TritonInfo);
    const mp1 = tritonInfo.fields[TritonInfoFieldId.MP1];
    const mp2 = tritonInfo.fields[TritonInfoFieldId.MP2];

    // we do not allow user to pick mp2 when only one or none sensors selected
    if (index === 0 && mp1.value === 1 && mp2.value === null) {
        mp1.value = 0;

        return;
    }

    const newMpValue = index === 0 ? mp1.value : mp2.value;
    const sensorSection = formState.config.sections.find(
        (section) => section.metadata?.type === SensorTypes.triton && section.metadata.sensorIndex === index,
    );
    if (sensorSection) {
        sensorSection.metadata.mp = newMpValue;

        const sensorKey = getSensorKeyById(sensorSection.id);
        sensorSection.title = newMpValue === 0 ? Mp1SensorTitles[sensorKey] : Mp2SensorTitles[sensorKey];
    }
}

export function checkTritonSameSensorError(formState: NormalizedFormState) {
    const tritonInfo = formState.config.sections.find((section) => section.id === TritonSectionIds.TritonInfo);

    const sameErrorMessage = tritonInfo.fields[TritonInfoFieldId.SameSensorError];
    const mp1 = tritonInfo.fields[TritonInfoFieldId.MP1];
    const mp2 = tritonInfo.fields[TritonInfoFieldId.MP2];

    if (mp1.value !== mp2.value) {
        sameErrorMessage.visible = false;

        tritonInfo.errors = null;
        return;
    }

    const sensor1Value = tritonInfo.fields[TritonInfoFieldId.Sensor1].value;
    const sensor2Value = tritonInfo.fields[TritonInfoFieldId.Sensor2].value;

    const allSensors = [...PrimarySensors, ...SecondarySensors];
    const sensor1 = allSensors.find((v) => v.value === sensor1Value);
    const sensor2 = allSensors.find((v) => v.value === sensor2Value);

    if (!sensor1 || !sensor2 || sensor1.text !== sensor2.text) {
        sameErrorMessage.visible = false;
        tritonInfo.errors = null;

        return;
    }

    tritonInfo.errors = {
        sameSensor: '',
    };

    sameErrorMessage.visible = true;
}

export function getParameterValue(installationShape: number) {
    if (!installationShape) {
        return null;
    }

    if (installationShape >= 200 && installationShape < 300) {
        return QCONTINUITY_ENTITY;
    } else if (installationShape >= 300 && installationShape < 400) {
        return QWEIR_ENTITY;
    } else if (installationShape >= 400 && installationShape < 500) {
        return QFLUME_ENTITY;
    } else if (installationShape >= 500 && installationShape < 600) {
        return QLOOKUP_ENTITY;
    }
}
